import React, { useEffect, useMemo } from 'react'
import { ThemeProvider } from 'styled-components'
import { useLocation } from 'react-router-dom'
import defaultTheme from '../../styles/theme'
import { generateCorporateColors } from '../../utils/themeColorGenerator'

interface DynamicThemeProviderProps {
  children: React.ReactNode
}

/**
 * DynamicThemeProvider wraps the styled-components ThemeProvider
 * and adjusts theme colors based on the current route.
 *
 * For routes starting with '/corporate', it applies custom color schemes:
 * - Primary color: #ce1c5b (with generated shades 100-900)
 * - Gray color: #f3f1e7 (with generated shades 100-900)
 */
export const DynamicThemeProvider: React.FC<DynamicThemeProviderProps> = ({
  children
}) => {
  const location = useLocation()

  const theme = useMemo(() => {
    const isCorporateRoute = location.pathname.startsWith('/corporate')

    if (isCorporateRoute) {
      // Generate corporate color scales
      const corporateColors = generateCorporateColors({
        primaryColor: '#ce1c5b',
        secondaryColor: '#212B41'
      })

      // Return modified theme with corporate colors
      return {
        ...defaultTheme,
        colors: {
          ...defaultTheme.colors,
          ...corporateColors
        }
      }
    }

    // Return default theme for non-corporate routes
    return defaultTheme
  }, [location.pathname])

  useEffect(() => {
    document.documentElement.classList.add('corporate')
  }, [])

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}
